import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import {makeStyles, Theme} from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import {AppStateType} from "../../../redux/reducers";
import QuestionItem from "./QuestionItem"
import {Button} from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import {useTranslation} from "react-i18next";
import styled from "styled-components/macro";
import {checkDependsCondition, getTextByCurrentLanguage} from "../../../utils/functions";

const Title = styled(Typography)`
    ${(props) => props.theme.breakpoints.down("sm")} {
      font-size: 14px;
    }
`

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`scrollable-auto-tabpanel-${index}`}
            aria-labelledby={`scrollable-auto-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3} bgcolor='#1B2635'>
                    {children}
                </Box>
            )}
        </div>
    );
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        flexGrow: 1,
        width: '100%',
        // backgroundColor: theme.palette.background.paper,
    },
    tabsWrapper: {
        display: 'inline-grid',
    },
}));

type TabsType = {
    existingUnit?: any,
    units?: any,
    unityIndex?: number | null,
}

const renderTabs = (unit: UnitStandard, a11yes: any[], unitId: string) => {
    const filteredUnits = unit.values.filter((item) => {
        return item.values.filter((item2) => checkDependsCondition(a11yes, item2.depends_on)).length > 0
    })

    return filteredUnits.map((item) => {
        return <Tab label={item.title} key={item.key} value={item.index} />
    })
}

const renderTabPanels = (unit: UnitStandard, tab: number, unitId: any) => {
    return unit.values.map((item: any, index: number) => {
        return (
            <TabPanel value={tab} index={index} key={index}>
                <QuestionItem
                  item={item}
                  key={index}
                  unitId={unitId}
                />
            </TabPanel>
        )
    })
}

type UnitStandard = {
    is_composite_only: boolean,
    is_mandatory: boolean,
    parents: Array<{ is_mandatory: boolean, unit_key: string }>
    key: string,
    title: string
    type: string
    values: Array<{
        key: string,
        title: string,
        type: string,
        index: number // it is not real index
        values: Array<{
            clps: any[]
            data_type: string
            depends_on: { [key: string]: string[] }
            disability_types: string[]
            disable_for_composite_unit: boolean
            is_mandatory: boolean
            is_mandatory_image: boolean
            key: string
            q_id: string
            q_title: string
            title: string
            type: string
            values: Array<{
                accessibility_levels: any[]
                key: string
                title: string
                type: string
            }>
        }>
    }>
}

type A11yStandard = {
    key: string
    title: string
    type: string
    values: UnitStandard[]
}

export const UnitStandardContext = React.createContext<any>(null!)
export default function QuestionTabs({ existingUnit, units, unityIndex }: TabsType) {
    const classes = useStyles();
    const { t } = useTranslation();
    const property: any = useSelector((state: AppStateType) => state.propertyReducer.property)
    const a11yStandard: A11yStandard = useSelector((state: AppStateType) => state.a11yStandardReducer.a11yStandard.find((item: any) => {
        return item.key === property.a11y_standard
    }))
    const [value, setValue] = React.useState<number>(0);
    const [unitStandard, setUnitStandard] = useState<UnitStandard | null>(null)
    const a11yes: any = useSelector((state: AppStateType) => state.a11yesReducer.a11yes)

    useEffect(() => {
        const unitStandard = a11yStandard.values.find((unit) => unit.key === existingUnit.type)
        if (unitStandard) {
            const unitStandardWithValueIndex = {
                ...unitStandard,
                values: unitStandard.values.map((value, idx) => ({...value, index: idx}))
            }

            let filterFromDisabledQuestions = unitStandardWithValueIndex
            if (existingUnit && existingUnit.related_to.length > 0) {
                filterFromDisabledQuestions = {
                    ...filterFromDisabledQuestions,
                    values: filterFromDisabledQuestions.values.map((value) => ({
                        ...value,
                        values: value.values.filter((qValue) => !qValue.disable_for_composite_unit)
                    }))
                }
            }

            const filteredUnitsForValue = filterFromDisabledQuestions.values.filter((item) => (
              item.values.filter((item2) => checkDependsCondition(a11yes, item2.depends_on)).length > 0
            ))

            setValue(filteredUnitsForValue[0].index)
            setUnitStandard(filterFromDisabledQuestions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingUnit])
   
    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    const getUnitName = (existingUnit: any, units: any): string => {
        let unitName = ''
        if (existingUnit) {
            if (existingUnit.related_to.length === 0) {
                unitName = `${existingUnit.title}: ${getTextByCurrentLanguage(existingUnit.name)}`
            } else {
                const findRelatedUnit = units.find((u: any) => u.id === existingUnit.related_to[0])
                unitName = `${getTextByCurrentLanguage(existingUnit.name)}: ${getTextByCurrentLanguage(findRelatedUnit.name)}`
            }
        }
        return unitName
    }

    const realUnits = useMemo(() => {
        if (unitStandard && unitStandard.values.length > 0) {
            return unitStandard.values
                .map((item, index: number) => ({ item: item, realIndex: index }))
                .filter(({ item }) => !!item.values && item.values.filter((value: { depends_on: any; }) => checkDependsCondition(a11yes, value.depends_on)).length > 0);
        }
        return [];
    }, [unitStandard, a11yes]);

    return (
        <div className={classes.root}>
            <UnitStandardContext.Provider value={unitStandard}>
                <Box p={4} bgcolor="#233245" borderRadius="6px 6px 0 0">
                    <Title variant="h6">
                        {getUnitName(existingUnit, units)}
                    </Title>
                </Box>
                <AppBar position="static" color="default">
                    <div className={classes.tabsWrapper} id="tab-wrapper">
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            indicatorColor="primary"
                            textColor="primary"
                            scrollButtons="auto"
                            aria-label="scrollable auto tabs example"
                            variant="scrollable"
                            style={{ width: '100%', justifyContent: 'space-between' }}
                        >
                            {unitStandard && unitStandard.values ? renderTabs(unitStandard, a11yes, existingUnit.id) : null}
                        </Tabs>
                    </div>
                </AppBar>
                {unitStandard && unitStandard.values.length > 0 ? renderTabPanels(unitStandard, value, existingUnit.id) : null}
                {realUnits && realUnits.length && value !== realUnits[realUnits.length - 1].realIndex ? <Box p={4}>
                    <Button variant="contained" color="primary" endIcon={<ArrowRightAltIcon />} onClick={() => {
                        // @ts-ignore
                        const currentElementIndex = realUnits.indexOf(realUnits.find((el: any) => el.realIndex === value));
                        handleChange(null!, realUnits[currentElementIndex + 1].realIndex);
                    }}>
                        {t("generalWords_Next")}
                    </Button>
                </Box> : null
                }
            </UnitStandardContext.Provider>
        </div >
    );
}
